import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Globe, Home, HelpCircle, MessageSquare, User, Sparkles } from 'lucide-react';

// Flag emojis for each language
const languageFlags = {
  en: '🇺🇸',
  es: '🇪🇸',
  fr: '🇫🇷',
  ar: '🇸🇦',
  zh: '🇨🇳',
  vi: '🇻🇳',
  ko: '🇰🇷',
  tl: '🇵🇭'
};

const languageNames = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  ar: 'العربية',
  zh: '中文',
  vi: 'Tiếng Việt',
  ko: '한국어',
  tl: 'Tagalog'
};

export default function LanguageTour() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [previousLanguage, setPreviousLanguage] = useState(() => i18n.language);

  useEffect(() => {
    // Listen for language changes
    const handleLanguageChange = (newLang) => {
      const lastShownLanguage = localStorage.getItem('lastTourLanguage');
      const tourShownCount = parseInt(localStorage.getItem('tourShownCount') || '0');
      
      // Only show tour if:
      // 1. It's a different language than previously shown
      // 2. We haven't shown it too many times (max 3 times per session)
      // 3. It's not the initial load (previousLanguage exists)
      if (previousLanguage && newLang !== previousLanguage && tourShownCount < 3) {
        setIsOpen(true);
        localStorage.setItem('lastTourLanguage', newLang);
        localStorage.setItem('tourShownCount', String(tourShownCount + 1));
      }
      
      setPreviousLanguage(newLang);
    };
    
    // Subscribe to language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, previousLanguage]);

  // Reset tour count on page load
  useEffect(() => {
    localStorage.setItem('tourShownCount', '0');
  }, []);

  const currentLang = i18n.language;
  const flag = languageFlags[currentLang] || '🌐';
  const langName = languageNames[currentLang] || currentLang.toUpperCase();

  const keyPhrases = [
    { icon: <Home className="w-4 h-4" />, key: 'nav.home', label: t('nav.home', 'Home') },
    { icon: <HelpCircle className="w-4 h-4" />, key: 'nav.chat', label: t('nav.chat', 'Get AI Help') },
    { icon: <MessageSquare className="w-4 h-4" />, key: 'nav.messages', label: t('nav.messages', 'Messages') },
    { icon: <User className="w-4 h-4" />, key: 'profile.title', label: t('profile.title', 'Profile') },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md" data-testid="language-tour-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <span className="text-3xl">{flag}</span>
            <span>{t('languageTour.title', 'Language Changed!')}</span>
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {t('languageTour.welcome', 'Welcome')} - {langName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            {t('languageTour.keyPhrases', 'Key phrases in your language:')}
          </p>
          
          <div className="space-y-3">
            {keyPhrases.map((phrase, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border transition-all hover:bg-muted"
              >
                <div className="p-2 bg-primary/10 rounded-full text-primary">
                  {phrase.icon}
                </div>
                <span className="font-medium">{phrase.label}</span>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={() => setIsOpen(false)} 
            className="w-full"
            data-testid="language-tour-dismiss"
          >
            {t('languageTour.gotIt', 'Got it!')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
